/**
 * React hook for fetching invoice data for shipments using React Query
 * 
 * This hook provides a convenient way to fetch and work with invoice charges
 * that were previously accessed via shipment.charges
 */

import { UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  ShipmentCharge,
  useGetInvoiceForShipmentQuery,
  useGetInvoicesForShipmentsQuery,
  GetInvoiceForShipmentQuery,
  GetInvoicesForShipmentsQuery,
  Exception,
} from '../graphql/generated';
import {
  getChargesForShipment,
  calculateShipmentChargeTotal,
  findInvoiceForShipment,
  isConsolidatedInvoice,
  getInvoiceStatusText,
} from '../utils/invoiceCharges';

// Type alias for the invoices data returned by queries
type InvoicesData = GetInvoicesForShipmentsQuery['invoices']['data'];

/**
 * Hook to fetch invoice for a single shipment using React Query
 * 
 * @param shipmentId - The shipment ID
 * @param options - React Query options
 * @returns Invoice data and helper functions
 */
export function useShipmentInvoice(
  shipmentId: string | null | undefined,
  options?: Omit<UseQueryOptions<GetInvoiceForShipmentQuery, Exception>, 'queryKey' | 'queryFn'>
) {
  const { data, isLoading, error, refetch } = useGetInvoiceForShipmentQuery(
    { shipmentId: shipmentId! },
    {
      enabled: !!shipmentId,
      ...options,
    }
  );

  const invoice = useMemo(() => {
    return data?.invoices?.data?.[0];
  }, [data]);

  const charges = useMemo(() => {
    if (!invoice || !shipmentId) return [];
    return getChargesForShipment(invoice, shipmentId);
  }, [invoice, shipmentId]);

  const total = useMemo(() => {
    if (!invoice || !shipmentId) return 0;
    return calculateShipmentChargeTotal(invoice, shipmentId);
  }, [invoice, shipmentId]);

  const isConsolidated = useMemo(() => {
    return isConsolidatedInvoice(invoice);
  }, [invoice]);

  const statusText = useMemo(() => {
    return getInvoiceStatusText(invoice);
  }, [invoice]);

  return {
    invoice,
    charges,
    total,
    isConsolidated,
    statusText,
    loading: isLoading,
    error,
    refetch,
  };
}

/**
 * Hook to fetch invoices for multiple shipments using React Query
 * 
 * @param shipmentIds - Array of shipment IDs
 * @param options - React Query options
 * @returns Invoices data and helper functions
 */
export function useShipmentInvoices(
  shipmentIds: string[] | null | undefined,
  options?: Omit<UseQueryOptions<GetInvoicesForShipmentsQuery, Exception>, 'queryKey' | 'queryFn'>
) {
  const { data, isLoading, error, refetch } = useGetInvoicesForShipmentsQuery(
    { shipmentIds: shipmentIds! },
    {
      enabled: !!shipmentIds && shipmentIds.length > 0,
      ...options,
    }
  );

  const invoices = useMemo(() => {
    return data?.invoices?.data || [];
  }, [data]);

  /**
   * Get charges for a specific shipment
   */
  const getCharges = useMemo(() => {
    return (shipmentId: string): ShipmentCharge[] => {
      const invoice = findInvoiceForShipment(invoices, shipmentId);
      return getChargesForShipment(invoice, shipmentId);
    };
  }, [invoices]);

  /**
   * Get total charges for a specific shipment
   */
  const getTotal = useMemo(() => {
    return (shipmentId: string): number => {
      const invoice = findInvoiceForShipment(invoices, shipmentId);
      return calculateShipmentChargeTotal(invoice, shipmentId);
    };
  }, [invoices]);

  /**
   * Get invoice for a specific shipment
   */
  const getInvoice = useMemo(() => {
    return (shipmentId: string): InvoicesData[0] | undefined => {
      return findInvoiceForShipment(invoices, shipmentId);
    };
  }, [invoices]);

  return {
    invoices,
    getCharges,
    getTotal,
    getInvoice,
    loading: isLoading,
    error,
    refetch,
  };
}
